import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageAlert as BeamPageAlert, Button, StandAloneLink } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import { ROUTES, StackLink } from '@app/router/constants';
import { useNavigate, useResumeServiceAlertIdentification } from '@app/hooks';
import { Alert, AlertAction, AlertButton, AlertType, DisplayLocation, removeAlert, setAlerts } from '@app/redux/alerts';
import { Route, setBillingNav } from '@app/redux/billingNav';
import { selectHasCanceledDisconnectOrder, selectIsResumeServiceLoading, setCancelOrder } from '@app/redux/disconnect';
import { openInNewTab, postOverviewMessage } from '@app/redux/utils';
import { saveAlertNameInStorage } from '@app/utils';
import { EventTypes } from '@app/types';

import { NotificationLevel } from '@mfe/shared/schema-types';

import { SpinnerButton } from '../SpinnerButton';
import { useAlertDescription } from './useAlertDescription';

import styles from './styles.module.scss';

type PageAlertProps = {
  setBuyMoreDialog?: () => void;
  navigateToPage?: (link: string) => void;
  loading?: boolean;
  alert: Alert;
};

export const PageAlert = (props: PageAlertProps) => {
  const { alert } = props;

  const alertDescription = useAlertDescription(alert);
  const dispatch = useDispatch();
  const isResumeServiceLoading = useSelector(selectIsResumeServiceLoading);

  if (!alert) {
    return null;
  }

  return (
    <BeamPageAlert
      className={`alerts-section ${styles['alerts-section']}`}
      data-cy="user-state-alert"
      title={alert.title}
      variant={alert.level}
      description={alertDescription}
      preventDefaultOnClose={true}
      onClose={() => {
        saveAlertNameInStorage(alert.type);
        if (!isResumeServiceLoading) {
          dispatch(removeAlert(alert.type));
        }
      }}
    >
      <AlertButtons {...props} />
    </BeamPageAlert>
  );
};

type PageAlertButtonsProps = {
  alert: Alert;
  setBuyMoreDialog?: () => void;
  navigateToPage?: (link: string) => void;
};

export const AlertButtons = (props: PageAlertButtonsProps) => {
  const { setBuyMoreDialog, navigateToPage, alert } = props;

  const { goTo } = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isResumeServiceAlert = useResumeServiceAlertIdentification(alert);
  const hasCanceledDisconnectOrder = useSelector(selectHasCanceledDisconnectOrder);
  const isResumeServiceLoading = useSelector(selectIsResumeServiceLoading);

  React.useEffect(() => {
    if (hasCanceledDisconnectOrder) {
      dispatch(removeAlert(alert.type));

      dispatch(
        setAlerts([
          {
            level: NotificationLevel.Success,
            title: t('Disconnect:successAlert.title'),
            caption: t('Disconnect:successAlert.description'),
            displayLocation: DisplayLocation.GLOBAL,
            type: AlertType.DisconnectSuccess,
          },
        ])
      );
    }
  }, [alert.type, dispatch, hasCanceledDisconnectOrder, t]);

  if (!alert) {
    return null;
  }

  const resumeServiceHandleClick = () => {
    dispatch(setCancelOrder());
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>, alert: Alert) => {
    if (
      alert.button?.action === AlertAction.RescheduleServiceCall ||
      alert.button?.action === AlertAction.ScheduleInstall
    ) {
      goTo(StackLink.ScheduleInstallation);
    }

    if (alert.button) {
      getButtonAction(event, goTo, dispatch, alert.button, navigateToPage, setBuyMoreDialog);
    }
  };

  const handleLinkClick = (event: React.MouseEvent<HTMLElement>, alert: Alert) => {
    if (alert.link) {
      getLinkAction(event, goTo, dispatch, alert.link, navigateToPage);
    }
  };

  if (!alert.button && !alert.link) {
    return null;
  }

  if (isResumeServiceAlert) {
    return (
      <div className={styles['buttonsWrapper']}>
        <SpinnerButton
          loading={isResumeServiceLoading}
          handleClick={resumeServiceHandleClick}
          label={t('Disconnect:resumeService.spinnerButton.text')}
          variant="secondary"
        />
      </div>
    );
  }

  return (
    <div className={styles['buttonsWrapper']}>
      {alert.button && (
        <Button
          data-cy="alert-button"
          variant="secondary"
          buttonSize="small"
          onClick={(event: React.MouseEvent<HTMLElement>) => handleButtonClick(event, alert)}
        >
          {alert.button?.label}
        </Button>
      )}
      {alert.link && (
        <StandAloneLink
          variant="secondary"
          href="#"
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            handleLinkClick(event, alert);
          }}
          linkSize="small"
          className={styles['alertsLink']}
          underline={true}
        >
          {alert.link?.label}
        </StandAloneLink>
      )}
    </div>
  );
};

function getButtonAction(
  event: React.MouseEvent<HTMLElement>,
  goTo: (url: string) => void,
  dispatch: React.Dispatch<any>,
  button: AlertButton,
  navigateToPage?: (link: string) => void,
  setBuyMoreDialog?: () => void
) {
  switch (button?.action) {
    case AlertAction.SignElectronicCustomerAgreement:
      if (navigateToPage) {
        navigateToPage(ROUTES.SIGN_CONTRACT);
      } else {
        goTo(ROUTES.SIGN_CONTRACT);
      }
      break;
    case AlertAction.GoToBilling:
      if (navigateToPage) {
        navigateToPage(ROUTES.BILLING);
      }
      dispatch(setBillingNav({ route: Route.OneTimePayment }));
      break;
    case AlertAction.BuyMore:
      if (setBuyMoreDialog) {
        pushToAdobeDataLayer({
          event: DataLayerEventName.FORM_START,
          event_name: DataLayerEventName.FORM_START,
          event_label: `${AnalyticsEventNames.BuyMoreInitiated}`,
          event_category: Categories.BuyMore,
        });
        setBuyMoreDialog();
        dispatch(postOverviewMessage({ eventType: EventTypes.BuyMore }));
      }
      break;
    case AlertAction.PlanUsage:
      dispatch(postOverviewMessage({ eventType: EventTypes.VisitPlanUsage }));
      if (navigateToPage) {
        navigateToPage('PlanUsage');
      }
      break;
    case AlertAction.Refresh:
      window.location.reload();
      break;
    case undefined:
      if (button.url) {
        if (!button.openInSameTab) {
          event.preventDefault();
          dispatch(openInNewTab(button.url));
        } else {
          goTo(button.url);
        }
      }
      break;
  }
}

function getLinkAction(
  event: React.MouseEvent<HTMLElement>,
  goTo: (url: string) => void,
  dispatch: React.Dispatch<any>,
  link: AlertButton,
  navigateToPage?: (link: string) => void
) {
  if (!link.action && link.url) {
    if (!link.openInSameTab) {
      event.preventDefault();
      dispatch(openInNewTab(link.url));
    }
  }

  if (link.action) {
    return getButtonAction(event, goTo, dispatch, link, navigateToPage);
  }
}
